/* eslint-disable @typescript-eslint/no-explicit-any */
import { MouseEvent } from 'react';
import { FormikProps, useFormik } from 'formik';
import {
    Button,
    CheckboxOnChangeData,
    Field,
    InfoLabel,
    Input,
    makeStyles,
    mergeClasses,
    Text,
    Tooltip,
} from '@fluentui/react-components';
import { AddSquare16Regular, Briefcase24Regular } from '@fluentui/react-icons';
import { Column, Row } from '../../common';
import { CaseDataTypeControl, CaseStatusTypeControl } from '../../controls/CaseDataTypeControl';
import { useToasts } from '../../toasts/useToasts';
import { useCaseDataService } from '../../../data/services/useCaseDataService';
import { SettingsPageTitle } from './SettingsPageTitle';
import { useCommonStyles } from '../../common/Styles/commonStyles';
import { Account, CaseStatus, CaseSystemStatus } from '../../../data/types';
import { AddEditCaseStatusDialog } from './AddEditCaseStatusDialog';
import { appTokens } from '../../common/Styles/appTokens';
import { useValidation } from '../../../data/types/validation';
import { Section } from '../../common/Section';

const useStyles = makeStyles({
    addBtn: {
        fontSize: '13px',
        paddingBlockStart: '0',
        paddingBlockEnd: '0',
    },
});

export type CaseTypePanelProps = {
    caseTypes: string[];
    onAddCaseType: (caseType: string) => Promise<void>;
    onRemoveCaseType: (caseType: string) => Promise<void>;
};
export const CaseTypePanel = (props: CaseTypePanelProps) => {
    const { caseTypes = [], onAddCaseType, onRemoveCaseType } = props;

    //Hooks
    const toasts = useToasts();
    const { validateCaseType } = useValidation();

    //Form
    const caseTypeForm = useFormik({
        initialValues: { type: '' },
        onSubmit: async (values, { resetForm }) => {
            const newCaseType = values.type.trim();

            try {
                !!onAddCaseType && onAddCaseType(newCaseType);

                toasts.caseTypes.notifyCaseTypeCreated(newCaseType);
            } catch {
                toasts.caseTypes.notifyCaseTypeCreateError(newCaseType);
            }
            resetForm();
        },
        validationSchema: validateCaseType.schema,
    });

    //UI Events
    const clickRemove = async (caseTypeToRemove: string) => {
        try {
            !!onRemoveCaseType && onRemoveCaseType(caseTypeToRemove);

            toasts.caseTypes.notifyCaseTypeRemoved(caseTypeToRemove);
        } catch {
            toasts.caseTypes.notifyCaseTypeRemovalError(caseTypeToRemove);
        }
    };
    const onKeyUp = (event: any) => {
        if (event.key === 'Enter') {
            caseTypeForm.submitForm();
        }
    };
    const onAddCaseTypeClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        caseTypeForm.submitForm();
    };
    const getRemoveCaseTypeHandler = (caseType: string) => () => clickRemove(caseType);

    return (
        <Section title='Case Types' flex='1' padding='px-2 pb-1'>
            <Column flex='1' gap='medium'>
                <Row noWrap height='auto'>
                    <Column flex='1'>
                        <Field label='New Case Type' required validationMessage={caseTypeForm.errors.type}>
                            <Input
                                name='type'
                                value={caseTypeForm?.values.type}
                                onChange={caseTypeForm?.handleChange}
                                onKeyUp={onKeyUp}
                                maxLength={validateCaseType.type.maxLength}
                                data-testid='case-type-panel-type-input'
                            />
                        </Field>
                    </Column>
                    <Column width='auto' vAlign='bottom'>
                        <Button
                            onClick={onAddCaseTypeClick}
                            disabled={!caseTypeForm?.values.type || !caseTypeForm.isValid || !caseTypeForm.dirty}
                            data-testid='case-type-panel-add-button'
                        >
                            Add
                        </Button>
                    </Column>
                </Row>
                <Column noGap maxHeight={appTokens.pageHeight('14rem')} scroll>
                    {!!caseTypes?.length &&
                        [...caseTypes]
                            .sort()
                            .map((caseType) => (
                                <CaseDataTypeControl
                                    key={caseType}
                                    dataType={caseType}
                                    onRemove={getRemoveCaseTypeHandler(caseType)}
                                />
                            ))}
                </Column>
            </Column>
        </Section>
    );
};

export type CaseStatusPanelProps = {
    accountForm?: FormikProps<Account>;
};
export const CaseStatusPanel = (props: CaseStatusPanelProps) => {
    const { accountForm } = props;

    //Hooks
    const toasts = useToasts();
    const caseDataService = useCaseDataService();
    const caseStatuses = caseDataService.userCreatedStatuses;

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const addBtnClasses = mergeClasses(styles.addBtn, commonStyles.button.root);

    //Logic
    const sortStatuses = (a: CaseStatus, b: CaseStatus) => {
        if (a.order == b.order) {
            return a.status.localeCompare(b.status);
        } else {
            return (a.order || 0) - (b.order || 0);
        }
    };
    const removeStatus = async (caseStatusToRemove: CaseStatus) => {
        try {
            await caseDataService.removeCaseStatus(caseStatusToRemove);

            toasts.caseStatus.notifyCaseStatusRemoved(`${caseStatusToRemove.status} (${caseStatusToRemove.systemStatus})`);
        } catch {
            toasts.caseStatus.notifyCaseStatusRemovalError(
                `${caseStatusToRemove.status} (${caseStatusToRemove.systemStatus})`,
            );
        }
    };
    const submitCaseStatus = async (caseStatus: CaseStatus, toast: boolean = true) => {
        try {
            await caseDataService.addCaseStatus(caseStatus);

            !!toast && toasts.caseStatus.notifyCaseStatusCreated(`${caseStatus.status} (${caseStatus.systemStatus})`);
        } catch {
            !!toast && toasts.caseStatus.notifyCaseStatusCreateError(`${caseStatus.status} (${caseStatus.systemStatus})`);
        }
    };

    //UI Events
    const onUpdateStatusSettingsClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        accountForm?.submitForm();
    };
    const onGroupStatusesChange = (e: any, data: CheckboxOnChangeData) => {
        e.stopPropagation();
        accountForm?.setFieldValue('settings.groupStatuses', data.checked);
    };
    const moveStatusUp = async (caseStatus: CaseStatus) => {
        if (!caseStatus || caseStatus.order === 0) return;
        caseDataService.moveStatusToIndex(caseStatus, (caseStatus.order || 0) - 1);
    };
    const moveStatusDown = async (caseStatus: CaseStatus) => {
        if (!caseStatus) return;
        const statsLength =
            caseDataService.caseStatuses.filter((x) => x.systemStatus === caseStatus.systemStatus).length || 0;
        if (caseStatus.order === undefined || caseStatus.order >= statsLength - 1) return;

        caseDataService.moveStatusToIndex(caseStatus, (caseStatus.order || 0) + 1);
    };

    const SystemStatus = (props: { sysStatus: CaseSystemStatus; info: string }) => {
        return (
            <Row gap='small'>
                <Column align='right' width='auto' vAlign='middle'>
                    <AddEditCaseStatusDialog systemStatus={props.sysStatus}>
                        <Tooltip content={`Create new '${props.sysStatus}' status`} relationship='description'>
                            <Button
                                className={addBtnClasses}
                                icon={<AddSquare16Regular />}
                                appearance='transparent'
                                data-testid={`add-case-status-${props.sysStatus}`}
                            />
                        </Tooltip>
                    </AddEditCaseStatusDialog>
                </Column>
                <Column vAlign='middle'>
                    <InfoLabel info={props.info}>{props.sysStatus}</InfoLabel>
                </Column>
            </Row>
        );
    };

    return (
        <Section
            title='Case Statuses'
            flex='1'
            padding='px-2 pb-1'
            scroll={false}
            action={
                <>
                    {!!accountForm?.dirty && (
                        <Button
                            appearance='primary'
                            size='small'
                            onClick={onUpdateStatusSettingsClick}
                            data-testid='case-status-panel-update-button'
                        >
                            Update
                        </Button>
                    )}
                </>
            }
        >
            <Column flex='1' gap='medium' maxHeight='calc(100% - 1.5rem)'>
                {/* {accountForm?.values.settings?.groupStatuses != null && (
                    <Column height='auto'>
                        <InfoLabel
                            info={
                                <Column gap='medium'>
                                    <Column noGap>
                                        <Text size={200}>
                                            When enabled, all custom statuses will appear grouped under their corresponding
                                            system default categories in the Case Status dropdown.
                                        </Text>
                                        <Text size={200} italic>
                                            e.g. New, Active, Completed, Closed, Removed
                                        </Text>
                                    </Column>
                                    <Text size={200}>
                                        When disabled, all statuses will display together in a single ungrouped list.
                                    </Text>
                                </Column>
                            }
                        >
                            <Checkbox
                                checked={accountForm?.values.settings?.groupStatuses}
                                onChange={onGroupStatusesChange}
                                label='Group Custom Statuses by System Status'
                                data-testid='group-custom-statuses-checkbox'
                            />
                        </InfoLabel>
                        <Divider style={{ marginBlock: '-.25rem' }} />
                    </Column>
                )} */}
                <Column gap='medium' maxHeight={appTokens.pageHeight('9.6rem')} scroll >
                    {/* New Statuses */}
                    <Column gap='small' height='auto' minHeight='fit-content'>
                        <SystemStatus
                            sysStatus={CaseSystemStatus.new}
                            info='System status denoting a newly created case. You can create custom statuses within this system status.'
                        />
                        <Column padding='pl-4' gap='small'>
                            {!!caseStatuses.length &&
                                caseStatuses
                                    .filter((x) => x.systemStatus == CaseSystemStatus.new)
                                    .sort(sortStatuses)
                                    .map((caseStatus, idx, arr) => (
                                        <CaseStatusTypeControl
                                            key={caseStatus.status}
                                            caseStatus={caseStatus}
                                            onUpdate={submitCaseStatus}
                                            onRemove={removeStatus}
                                            onMoveUp={moveStatusUp}
                                            onMoveDown={moveStatusDown}
                                            canMoveUp={idx !== 0}
                                            canMoveDown={idx !== arr.length - 1}
                                            data-testid={`case-status-control-${caseStatus.systemStatus}-${caseStatus.status}`}
                                        />
                                    ))}
                        </Column>
                    </Column>

                    {/* Active Statuses */}
                    <Column gap='small' height='auto' minHeight='fit-content'>
                        <SystemStatus
                            sysStatus={CaseSystemStatus.active}
                            info='System status denoting an Active case. You can create custom statuses within this system status.'
                        />
                        <Column padding='pl-4' gap='small'>
                            {!!caseStatuses.length &&
                                caseStatuses
                                    .filter((x) => x.systemStatus == CaseSystemStatus.active)
                                    .sort(sortStatuses)
                                    .map((caseStatus, idx, arr) => (
                                        <CaseStatusTypeControl
                                            key={caseStatus.status}
                                            caseStatus={caseStatus}
                                            onUpdate={submitCaseStatus}
                                            onRemove={removeStatus}
                                            onMoveUp={moveStatusUp}
                                            onMoveDown={moveStatusDown}
                                            canMoveUp={idx !== 0}
                                            canMoveDown={idx !== arr.length - 1}
                                            data-testid={`case-status-control-${caseStatus.systemStatus}-${caseStatus.status}`}
                                        />
                                    ))}
                        </Column>
                    </Column>

                    {/* Completed Statuses */}
                    <Column gap='small' height='auto' minHeight='fit-content'>
                        <SystemStatus
                            sysStatus={CaseSystemStatus.completed}
                            info='System status denoting a completed case. You can create custom statuses within this system status.'
                        />
                        <Column padding='pl-4' gap='small'>
                            {!!caseStatuses.length &&
                                caseStatuses
                                    .filter((x) => x.systemStatus == CaseSystemStatus.completed)
                                    .sort(sortStatuses)
                                    .map((caseStatus, idx, arr) => (
                                        <CaseStatusTypeControl
                                            key={caseStatus.status}
                                            caseStatus={caseStatus}
                                            onUpdate={submitCaseStatus}
                                            onRemove={removeStatus}
                                            onMoveUp={moveStatusUp}
                                            onMoveDown={moveStatusDown}
                                            canMoveUp={idx !== 0}
                                            canMoveDown={idx !== arr.length - 1}
                                            data-testid={`case-status-control-${caseStatus.systemStatus}-${caseStatus.status}`}
                                        />
                                    ))}
                        </Column>
                    </Column>

                    {/* Closed Statuses */}
                    <Column gap='small' height='auto' minHeight='fit-content'>
                        <SystemStatus
                            sysStatus={CaseSystemStatus.closed}
                            info='System status denoting a closed case. You can create custom statuses within this system status.'
                        />
                        <Column padding='pl-4' gap='small'>
                            {!!caseStatuses.length &&
                                caseStatuses
                                    .filter((x) => x.systemStatus == CaseSystemStatus.closed)
                                    .sort(sortStatuses)
                                    .map((caseStatus, idx, arr) => (
                                        <CaseStatusTypeControl
                                            key={caseStatus.status}
                                            caseStatus={caseStatus}
                                            onUpdate={submitCaseStatus}
                                            onRemove={removeStatus}
                                            onMoveUp={moveStatusUp}
                                            onMoveDown={moveStatusDown}
                                            canMoveUp={idx !== 0}
                                            canMoveDown={idx !== arr.length - 1}
                                            data-testid={`case-status-control-${caseStatus.systemStatus}-${caseStatus.status}`}
                                        />
                                    ))}
                        </Column>
                    </Column>

                    {/* Removed Statuses */}
                    <Column gap='small' height='auto' minHeight='fit-content'>
                        <SystemStatus
                            sysStatus={CaseSystemStatus.removed}
                            info='System status denoting a removed case. You can create custom statuses within this system status.'
                        />
                        <Column padding='pl-4' gap='small'>
                            {!!caseStatuses.length &&
                                caseStatuses
                                    .filter((x) => x.systemStatus == CaseSystemStatus.removed)
                                    .sort(sortStatuses)
                                    .map((caseStatus, idx, arr) => (
                                        <CaseStatusTypeControl
                                            key={caseStatus.status}
                                            caseStatus={caseStatus}
                                            onUpdate={submitCaseStatus}
                                            onRemove={removeStatus}
                                            onMoveUp={moveStatusUp}
                                            onMoveDown={moveStatusDown}
                                            canMoveUp={idx !== 0}
                                            canMoveDown={idx !== arr.length - 1}
                                            data-testid={`case-status-control-${caseStatus.systemStatus}-${caseStatus.status}`}
                                        />
                                    ))}
                        </Column>
                    </Column>
                </Column>
            </Column>
        </Section>
    );
};

export type CaseExpenseTypePanelProps = {
    expenseTypes: string[];
    onAddExpenseType: (expenseType: string) => Promise<void>;
    onRemoveExpenseType: (expenseType: string) => Promise<void>;
};
export const CaseExpenseTypePanel = (props: CaseExpenseTypePanelProps) => {
    const { expenseTypes = [], onAddExpenseType, onRemoveExpenseType } = props;

    //Hooks
    const toasts = useToasts();
    const { validateCaseExpenseType } = useValidation();

    //Form
    const expenseTypeForm = useFormik({
        initialValues: { expenseType: '' },
        onSubmit: async (values, { resetForm }) => {
            try {
                !!onAddExpenseType && onAddExpenseType(values.expenseType);

                toasts.expenseTypes.notifyExpenseTypeCreated(values.expenseType);
            } catch {
                toasts.expenseTypes.notifyExpenseTypeCreateError(values.expenseType);
            }
            resetForm();
        },
        validationSchema: validateCaseExpenseType.schema,
    });

    //UI Events
    const clickRemove = async (expenseType: string) => {
        try {
            !!onRemoveExpenseType && onRemoveExpenseType(expenseType);

            toasts.expenseTypes.notifyExpenseTypeRemoved(expenseType);
        } catch {
            toasts.expenseTypes.notifyExpenseTypeRemovalError(expenseType);
        }
    };
    const getRemoveExpenseTypeHandler = (expenseType: string) => () => clickRemove(expenseType);
    const onAddExpenseTypeClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        expenseTypeForm.submitForm();
    };
    const onKeyUp = (event: any) => {
        if (event.key === 'Enter') {
            expenseTypeForm.submitForm();
        }
    };

    return (
        <Section title='Expense Types' flex='1' padding='px-2' scroll={false}>
            <Column flex='1' gap='medium'>
                <Row noWrap>
                    <Column flex='1'>
                        <Field label='New Expense Type' required validationMessage={expenseTypeForm.errors.expenseType}>
                            <Input
                                name='expenseType'
                                value={expenseTypeForm?.values.expenseType}
                                onChange={expenseTypeForm?.handleChange}
                                onKeyUp={onKeyUp}
                                maxLength={validateCaseExpenseType.text.maxLength}
                                data-testid='expense-type-panel-input'
                            />
                        </Field>
                    </Column>
                    <Column vAlign='bottom' width='auto'>
                        <Button
                            onClick={onAddExpenseTypeClick}
                            disabled={
                                !expenseTypeForm?.values.expenseType || !expenseTypeForm.isValid || !expenseTypeForm.dirty
                            }
                            data-testid='expense-type-panel-add-button'
                        >
                            Add
                        </Button>
                    </Column>
                </Row>
                <Column noGap maxHeight={appTokens.pageHeight('14rem')} scroll>
                    {!!expenseTypes.length &&
                        [...expenseTypes]
                            .sort()
                            .map((expenseType) => (
                                <CaseDataTypeControl
                                    key={expenseType}
                                    dataType={expenseType}
                                    onRemove={getRemoveExpenseTypeHandler(expenseType)}
                                />
                            ))}
                </Column>
            </Column>
        </Section>
    );
};

export type AccountSettingsCaseDataProps = {
    className?: string;
    accountForm?: FormikProps<Account>;
};

export const AccountSettingsCaseData = (props: AccountSettingsCaseDataProps) => {
    const { className, accountForm } = props;

    //Hooks
    const caseDataService = useCaseDataService();

    //Logic
    const onAddCaseType = async (caseType: string) => {
        caseDataService.addCaseType(caseType);
    };
    const onRemoveCaseType = async (caseType: string) => {
        caseDataService.removeCaseType(caseType);
    };
    const onAddExpenseType = async (expenseType: string) => {
        caseDataService.addExpenseType(expenseType);
    };
    const onRemoveExpenseType = async (expenseType: string) => {
        caseDataService.removeExpenseType(expenseType);
    };

    return (
        <Column className={className} gap='small' height={appTokens.pageHeight('5.25rem')}>
            <SettingsPageTitle
                title='Case Data Types'
                icon={<Briefcase24Regular />}
                infoLabel={
                    <Column noGap>
                        <Text size={300} weight='semibold'>
                            Create custom data types for cases.
                        </Text>
                        <Text style={{ paddingInlineStart: '.5rem' }}>- Custom case types</Text>
                        <Text style={{ paddingInlineStart: '.5rem' }}>- Custom case statuses</Text>
                        <Text style={{ paddingInlineStart: '.5rem' }}>- Custom expense types</Text>
                    </Column>
                }
            />
            <Column flex='1' scroll >
                <Row flex='1' height='auto'>
                    <Column flex='1' maxHeight={appTokens.pageHeight('7.25rem')} >
                        <CaseTypePanel
                            caseTypes={caseDataService?.caseTypes}
                            onAddCaseType={onAddCaseType}
                            onRemoveCaseType={onRemoveCaseType}
                        />
                    </Column>
                    <Column flex='1' maxHeight={appTokens.pageHeight('7.25rem')} >
                        <CaseStatusPanel accountForm={accountForm} />
                    </Column>

                    <Column flex='1' maxHeight={appTokens.pageHeight('7.25rem')} >
                        <CaseExpenseTypePanel
                            expenseTypes={caseDataService?.expenseTypes}
                            onAddExpenseType={onAddExpenseType}
                            onRemoveExpenseType={onRemoveExpenseType}
                        />
                    </Column>
                </Row>
            </Column>
        </Column>
    );
};
